import { createTheme } from "@mui/material";

const FONT = `'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

export const brandColors = {
  // Core accent
  indigo:     "#0EA5E9",
  indigoMid:  "#0EA5E9",
  indigoSoft: "rgba(14,165,233,0.12)",

  // Semantic
  emerald:    "#10B981",
  rose:       "#F43F5E",
  sky:        "#38BDF8",
  violet:     "#A78BFA",

  // Dark surfaces
  dark0:   "#0E1016",
  dark1:   "#151821",
  dark2:   "#1C2032",
  dark3:   "#252A3D",
  darkBorder: "rgba(255,255,255,0.07)",

  // Light surfaces
  light0:  "#F4F6F9",
  light1:  "#FFFFFF",
  light2:  "#EEF0F5",
  lightBorder: "#E2E5ED",

  // Legacy aliases kept so TopBar/other files don't break
  electricBlue: "#38BDF8",
  coral:        "#F43F5E",
  limeGreen:    "#10B981",
  vividViolet:  "#A78BFA",
  softWhite:    "#F4F6F9",
  charcoal:     "#1A1D2E",
  teal:         "#10B981",
  surface0:     "#0E1016",
  surface1:     "#151821",
  surface2:     "#1C2032",
  surfaceElevated: "#1C203280",
  borderSubtle: "rgba(255,255,255,0.07)",
  borderStrong: "rgba(255,255,255,0.12)",
};

// ─── Shared typography ────────────────────────────────────────────────────────
const typography = {
  fontFamily: FONT,
  fontSize: 15,
  fontWeightLight:   300,
  fontWeightRegular: 400,
  fontWeightMedium:  500,
  fontWeightBold:    700,

  h1: { fontFamily: FONT, fontWeight: 800, fontSize: "2.5rem",   lineHeight: 1.15, letterSpacing: "-0.03em" },
  h2: { fontFamily: FONT, fontWeight: 700, fontSize: "2rem",     lineHeight: 1.2,  letterSpacing: "-0.02em" },
  h3: { fontFamily: FONT, fontWeight: 700, fontSize: "1.5rem",   lineHeight: 1.25, letterSpacing: "-0.015em" },
  h4: { fontFamily: FONT, fontWeight: 700, fontSize: "1.25rem",  lineHeight: 1.3,  letterSpacing: "-0.01em" },
  h5: { fontFamily: FONT, fontWeight: 600, fontSize: "1.0625rem",lineHeight: 1.35 },
  h6: { fontFamily: FONT, fontWeight: 600, fontSize: "0.9375rem",lineHeight: 1.4  },
  subtitle1: { fontFamily: FONT, fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.4 },
  subtitle2: { fontFamily: FONT, fontWeight: 500, fontSize: "0.875rem",  lineHeight: 1.5 },
  body1:     { fontFamily: FONT, fontWeight: 400, fontSize: "0.9375rem", lineHeight: 1.6 },
  body2:     { fontFamily: FONT, fontWeight: 400, fontSize: "0.875rem",  lineHeight: 1.6 },
  caption:   { fontFamily: FONT, fontWeight: 500, fontSize: "0.75rem",   lineHeight: 1.5 },
  overline:  { fontFamily: FONT, fontWeight: 700, fontSize: "0.6875rem", lineHeight: 1.5, letterSpacing: "0.1em", textTransform: "uppercase" },
  button:    { fontFamily: FONT, fontWeight: 600, fontSize: "0.875rem",  textTransform: "none", letterSpacing: "0.005em" },
};

// ─── DARK THEME ───────────────────────────────────────────────────────────────
export const themeDark = createTheme({
  palette: {
    mode: "dark",
    primary:   { main: brandColors.indigo, light: "#7DD3FC", dark: "#0284C7", contrastText: "#fff" },
    secondary: { main: brandColors.emerald, light: "#34D399", dark: "#059669" },
    success:   { main: brandColors.emerald, light: "#34D399" },
    warning:   { main: brandColors.violet },
    error:     { main: brandColors.rose },
    info:      { main: brandColors.sky },
    background: { default: brandColors.dark0, paper: brandColors.dark1 },
    text: { primary: "#EAECF0", secondary: "#8A94A6", disabled: "#505869" },
    divider: brandColors.darkBorder,
    action: {
      hover:          "rgba(255,255,255,0.05)",
      selected:       "rgba(91,108,249,0.12)",
      disabledOpacity: 0.38,
    },
  },

  typography,

  shape: { borderRadius: 10 },
  spacing: 8,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brandColors.dark0,
          backgroundImage: "none",
        },
        "*::-webkit-scrollbar": { width: 6, height: 6 },
        "*::-webkit-scrollbar-track": { background: "transparent" },
        "*::-webkit-scrollbar-thumb": {
          background: "rgba(255,255,255,0.1)",
          borderRadius: 6,
          "&:hover": { background: "rgba(255,255,255,0.18)" },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: brandColors.dark1,
          border: `1px solid ${brandColors.darkBorder}`,
          boxShadow: "none",
          borderRadius: 10,
        },
        elevation1: { boxShadow: "0 1px 4px rgba(0,0,0,0.3)" },
        elevation2: { boxShadow: "0 2px 8px rgba(0,0,0,0.35)" },
        elevation4: { boxShadow: "0 4px 16px rgba(0,0,0,0.4)" },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: brandColors.dark1,
          border: `1px solid ${brandColors.darkBorder}`,
          borderRadius: 14,
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          transition: "border-color .2s ease, box-shadow .2s ease",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "7px 16px",
          fontWeight: 600,
          lineHeight: 1.5,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          background: brandColors.indigo,
          "&:hover": { background: "#0284C7" },
        },
        outlined: {
          borderColor: brandColors.darkBorder,
          "&:hover": {
            borderColor: brandColors.indigo,
            backgroundColor: brandColors.indigoSoft,
          },
        },
        sizeSmall:  { padding: "5px 12px", fontSize: "0.8125rem" },
        sizeLarge:  { padding: "10px 24px", fontSize: "0.9375rem" },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontWeight: 600,
          borderRadius: 6,
          height: 26,
          fontSize: "0.75rem",
        },
        label: { paddingLeft: 10, paddingRight: 10 },
        outlined: {
          borderColor: brandColors.darkBorder,
          color: "#9BA3B5",
        },
        filledPrimary: { backgroundColor: brandColors.indigoSoft, color: "#38BDF8" },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: "0.9rem", borderRadius: 8 },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.dark2,
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: brandColors.darkBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brandColors.indigo, borderWidth: 1.5 },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: "0.875rem" },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          fontFamily: FONT,
          fontSize: "0.75rem",
          fontWeight: 500,
          backgroundColor: brandColors.dark3,
          border: `1px solid ${brandColors.darkBorder}`,
          borderRadius: 6,
          padding: "6px 10px",
        },
        arrow: { color: brandColors.dark3 },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: brandColors.darkBorder } },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: "0.875rem",
          borderRadius: 6,
          margin: "1px 4px",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" },
          "&.Mui-selected": {
            backgroundColor: brandColors.indigoSoft,
            "&:hover": { backgroundColor: brandColors.indigoSoft },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { fontFamily: FONT, borderColor: brandColors.darkBorder },
        head: { fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#8A94A6" },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { fontFamily: FONT, borderRadius: 10, border: "1px solid" },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { backgroundImage: "none", borderRadius: 16, border: `1px solid ${brandColors.darkBorder}` },
      },
    },

    MuiBottomNavigation: {
      styleOverrides: {
        root: { height: 64, borderTop: `1px solid ${brandColors.darkBorder}`, backgroundColor: brandColors.dark1 },
      },
    },

    MuiBottomNavigationAction: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: "0.75rem", color: "#8A94A6", "&.Mui-selected": { color: brandColors.indigo } },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: brandColors.indigo, height: 2, borderRadius: 2 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: "0.875rem",
          textTransform: "none",
          color: "#8A94A6",
          minHeight: 42,
          "&.Mui-selected": { color: "#EAECF0", fontWeight: 600 },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: "rgba(255,255,255,0.07)" },
        bar:  { borderRadius: 4 },
      },
    },
  },
});

// ─── LIGHT THEME ─────────────────────────────────────────────────────────────
export const themeLight = createTheme({
  palette: {
    mode: "light",
    primary:   { main: brandColors.indigoMid, light: "#38BDF8", dark: "#0284C7", contrastText: "#fff" },
    secondary: { main: "#059669", light: "#10B981", dark: "#047857" },
    success:   { main: "#059669" },
    warning:   { main: brandColors.violet },
    error:     { main: "#DC2626" },
    info:      { main: "#0284C7" },
    background: { default: brandColors.light0, paper: brandColors.light1 },
    text: { primary: "#111827", secondary: "#6B7280", disabled: "#9CA3AF" },
    divider: brandColors.lightBorder,
    action: {
      hover:    "rgba(0,0,0,0.04)",
      selected: "rgba(79,70,229,0.08)",
    },
  },

  typography,

  shape: { borderRadius: 10 },
  spacing: 8,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: brandColors.light0, backgroundImage: "none" },
        "*::-webkit-scrollbar": { width: 6, height: 6 },
        "*::-webkit-scrollbar-track": { background: "transparent" },
        "*::-webkit-scrollbar-thumb": {
          background: "rgba(0,0,0,0.12)",
          borderRadius: 6,
          "&:hover": { background: "rgba(0,0,0,0.2)" },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "#FFFFFF",
          border: `1px solid ${brandColors.lightBorder}`,
          boxShadow: "none",
          borderRadius: 10,
        },
        elevation1: { boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
        elevation2: { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
        elevation4: { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "#FFFFFF",
          border: `1px solid ${brandColors.lightBorder}`,
          borderRadius: 14,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          transition: "border-color .2s ease, box-shadow .2s ease",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "7px 16px",
          fontWeight: 600,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          background: brandColors.indigoMid,
          "&:hover": { background: "#0284C7" },
        },
        outlined: {
          borderColor: brandColors.lightBorder,
          "&:hover": { borderColor: brandColors.indigoMid, backgroundColor: "rgba(79,70,229,0.05)" },
        },
        sizeSmall: { padding: "5px 12px", fontSize: "0.8125rem" },
        sizeLarge: { padding: "10px 24px", fontSize: "0.9375rem" },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontWeight: 600,
          borderRadius: 6,
          height: 26,
          fontSize: "0.75rem",
        },
        label: { paddingLeft: 10, paddingRight: 10 },
        outlined: { borderColor: brandColors.lightBorder, color: "#4B5563" },
        filledPrimary: { backgroundColor: "rgba(14,165,233,0.1)", color: "#0284C7" },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: "0.9rem", borderRadius: 8 },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: brandColors.lightBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brandColors.indigoMid, borderWidth: 1.5 },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: "0.875rem" },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          fontFamily: FONT,
          fontSize: "0.75rem",
          fontWeight: 500,
          backgroundColor: "#1F2937",
          borderRadius: 6,
          padding: "6px 10px",
        },
        arrow: { color: "#1F2937" },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: brandColors.lightBorder } },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: "0.875rem",
          borderRadius: 6,
          margin: "1px 4px",
          "&.Mui-selected": {
            backgroundColor: "rgba(79,70,229,0.08)",
            "&:hover": { backgroundColor: "rgba(79,70,229,0.12)" },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root:  { fontFamily: FONT, borderColor: brandColors.lightBorder },
        head: { fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#6B7280" },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { fontFamily: FONT, borderRadius: 10, border: "1px solid" },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { backgroundImage: "none", borderRadius: 16, border: `1px solid ${brandColors.lightBorder}` },
      },
    },

    MuiBottomNavigation: {
      styleOverrides: {
        root: { height: 64, borderTop: `1px solid ${brandColors.lightBorder}` },
      },
    },

    MuiBottomNavigationAction: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: "0.75rem" },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: brandColors.indigoMid, height: 2, borderRadius: 2 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: "0.875rem",
          textTransform: "none",
          color: "#6B7280",
          minHeight: 42,
          "&.Mui-selected": { color: "#111827", fontWeight: 600 },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4 },
        bar:  { borderRadius: 4 },
      },
    },
  },
});
